import { FormattedMessage, useIntl } from 'react-intl'
import _get from 'lodash/get';
import Title from '@/shared/ui/Title';
import Table, { DateCell, TextCell, TxHashCell } from '@/shared/ui/Table';
import DownloadCsv from '@/entities/DownloadCsv';
import StatusIcon from '@/shared/ui/StatusIcon';
import { openNewTab } from '@/shared/utils/openLink'
import NewTabLink from '@/shared/ui/NewTabLink';
import { formatChartDate } from '@/shared/utils/dateFormatters';
import { SORT_BY, getStatusIconVariant, getStatusMessage } from '~/sha256/Payouts';
import { useGetBalanceIdQuery, useGetTransactionsQuery } from '@/shared/api/Payouts';
import { useExplorer } from '@/processes/PoolStats';
import { useMinerAccount } from "@/processes/MinerAccounts";
import { useNavigation } from '@/processes/Router';

type PayoutsTableProps = {
  className?: string;
}

const PayoutsTable = (props: PayoutsTableProps) => {
  const { className } = props;
  const intl = useIntl();
  const { account, coin, miner } = useMinerAccount();
  const { explorerLink } = useExplorer({ coin });
  const { isBTC } = useNavigation();

  const {
    data: balanceId,
  } = useGetBalanceIdQuery({ account, coin, miner }, { skip: !(account || miner) })
  const {
    data: transactions = [],
    isSuccess: isTransactionsSuccess,
  } = useGetTransactionsQuery({
    coin,
    balanceId,
    account,
    miner,
    isSortingDESC: true,
    orderBy: SORT_BY.CREATED_AT,
  }, { skip: isBTC ? !balanceId : !coin })

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-6">
        <Title order={3}>
          <FormattedMessage id="payouts.history"/>
        </Title>

        <DownloadCsv
          prepareData={() => {
            const csvData = [
              [
                intl.formatMessage({ id: 'payouts.time' }),
                intl.formatMessage({ id: 'payouts.txid' }),
                intl.formatMessage({ id: 'payouts.total' }),
                intl.formatMessage({ id: 'payouts.status' }),
              ],
            ];
            transactions.forEach((transaction: any) => {
              const { value, createdAt, txHash, status } = transaction;
              const statusMessage = intl.formatMessage({ id: getStatusMessage(status) })
              const time = formatChartDate({ date: createdAt, locale: intl.locale });
              return csvData.push([time, txHash, value, statusMessage]);
            });
            return csvData;
          }}
          filename={`${coin} - ${intl.formatMessage({ id: 'payouts.history' })}`}
          title={intl.formatMessage({ id: 'download.csv' })}
        />
      </div>
      <Table
        stickyHeader={true}
        data={transactions}
        responsiveColspan={{
          md: 3,
        }}
        onRowClick={(row) => {
          row.txHash && openNewTab(explorerLink(row.txHash));
        }}
        columns={[
          {
            Header: intl.formatMessage({ id: 'payouts.time' }),
            accessor: 'createdAt',
            Cell: DateCell,
          },
          {
            Header: intl.formatMessage({ id: 'payouts.txid' }),
            accessor: 'txHash',
            disableSortBy: true,
            className: 'hidden md:table-cell',
            Cell: ({ value }: any) => <TxHashCell coin={coin} hash={value} explorer={explorerLink(value)}/>
          },
          {
            Header: intl.formatMessage({ id: 'payouts.total' }),
            accessor: 'value',
            Cell: ({ value }: any) => <TextCell value={value} subValue={coin}/>
          },
          {
            Header: intl.formatMessage({ id: 'payouts.status' }),
            accessor: 'status',
            Cell: (props: any) => {
              const { value, row, data } = props;
              const hash = _get(data, `${row.index}.txHash`, null);
              return (
                <span className='flex items-center justify-between'>
                  <StatusIcon
                    className='w-[14px] h-[14px]'
                    status={getStatusIconVariant(value)}
                    title={intl.formatMessage({ id: getStatusMessage(value) })}
                    titleClassName='hidden md:inline-block'
                  />
                  <div className="ml-2 w-6 inline-flex items-center">
                    {hash ? (
                      <NewTabLink/>
                    ) : null}
                  </div>
                </span>
              )
            }
          },
        ]}
      />
    </div>
  );
}

export default PayoutsTable;
